import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { fmt } from "@/lib/api";

export const NEEDS = ["unknown_sheet", "new_line", "new_loan", "new_ncf_row", "missing_item", "unclassified_row"];
const TITLES = {
  unknown_sheet: "New worksheet", new_line: "New line item", new_loan: "New loan facility", new_ncf_row: "New NCF row",
  missing_item: "Missing item", unclassified_row: "Unclassified row", check_failed: "Reconciliation", years_changed: "Financial years",
};
const TONE = { missing_item: "border-rose-500/30", unclassified_row: "border-rose-500/30", check_failed: "border-amber-500/30", years_changed: "border-amber-500/30" };
const LINE_SECTIONS = ["income", "expenses", "unit_capex", "interest", "project_capex"];
const ROLES = { entity: "Entity (unit sheet)", other_entity: "Other entity / corporate", inter_unit_funding: "Inter-unit funding", central_expense: "Central / HO expense" };

const Pick = ({ value, onChange, options, testid, w = "w-[200px]" }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className={`h-8 ${w} border-slate-700 bg-slate-900/70 text-xs`} data-testid={testid}><SelectValue placeholder="Choose…" /></SelectTrigger>
    <SelectContent className="bg-[#131c2e]">
      {Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Controls = ({ issue, value = {}, onChange, sections, aliasOptions, entityNames = [] }) => {
  const tid = `issue-${issue.id.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`;
  const set = (patch) => onChange({ ...value, ...patch });
  if (issue.type === "unknown_sheet") {
    const nm = (value.name || "").trim().toLowerCase();
    const dup = nm && entityNames.find((n) => n.toLowerCase() === nm);
    return (
      <div className="flex max-w-xl flex-wrap gap-2">
        <Pick value={value.action} onChange={(v) => set({ action: v })} options={{ include: "Include as entity", ignore: "Ignore sheet" }} testid={`${tid}-action`} />
        {value.action === "include" && <>
          <Input value={value.name || ""} onChange={(e) => set({ name: e.target.value })} placeholder="Display name" className="h-8 w-48 border-slate-700 bg-slate-900/70 text-xs" data-testid={`${tid}-name`} />
          <Pick value={value.category} onChange={(v) => set({ category: v })} options={{ Institutions: "Institutions", Corporates: "Corporates" }} testid={`${tid}-category`} w="w-[140px]" />
          <Input value={value.ncf_label || ""} onChange={(e) => set({ ncf_label: e.target.value })} placeholder="NCF row label" className="h-8 w-44 border-slate-700 bg-slate-900/70 text-xs" data-testid={`${tid}-ncf-label`} />
          {!value.ncf_label && <span className="w-full text-[11px] text-amber-400" data-testid={`${tid}-ncf-hint`}>Without an NCF row label this entity is shown but excluded from group totals.</span>}
          {dup && <span className="w-full text-[11px] font-medium text-rose-400" data-testid={`${tid}-duplicate-warning`}>“{dup}” already exists on the dashboard. Rename this entity to avoid two units with the same name.</span>}
        </>}
      </div>
    );
  }
  if (issue.type === "new_line") {
    const opts = { accept: `Accept under ${sections[issue.section]}`, move: "Move to another section", exclude: "Exclude from dashboard" };
    if (aliasOptions.length) opts.alias = "Rename of an existing item";
    return (
      <div className="flex flex-wrap gap-2">
        <Pick value={value.action} onChange={(v) => set({ action: v })} options={opts} testid={`${tid}-action`} w="w-[230px]" />
        {value.action === "move" && <Pick value={value.section} onChange={(v) => set({ section: v })} testid={`${tid}-section`}
          options={Object.fromEntries(LINE_SECTIONS.filter((x) => x !== issue.section).map((x) => [x, sections[x]]))} />}
        {value.action === "alias" && <Pick value={value.alias_of} onChange={(v) => set({ alias_of: v })} testid={`${tid}-alias`}
          options={Object.fromEntries(aliasOptions.map((x) => [x, x]))} />}
      </div>
    );
  }
  if (issue.type === "new_ncf_row") return <Pick value={value.role} onChange={(v) => set({ role: v, action: "accept" })} options={ROLES} testid={`${tid}-role`} w="w-[230px]" />;
  if (issue.type === "new_loan") return <span className="text-xs text-emerald-400">Will be added to loan register</span>;
  if (issue.type === "missing_item" || issue.type === "unclassified_row") {
    return (
      <label className="flex items-center gap-2 text-xs text-slate-300">
        <Checkbox checked={value.action === "acknowledge"} onCheckedChange={(c) => set({ action: c ? "acknowledge" : null })} data-testid={`${tid}-ack`} />
        Acknowledge
      </label>
    );
  }
  return null;
};

export const IssueCard = ({ issue, ...rest }) => (
  <div className={`rounded-lg border bg-slate-900/40 p-3.5 ${TONE[issue.type] || "border-sky-500/30"}`} data-testid={`review-issue-${issue.type}`}>
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <IssueHead issue={issue} />
      <Controls issue={issue} {...rest} />
    </div>
    {issue.type === "unknown_sheet" && <SheetPreview issue={issue} sections={rest.sections} />}
  </div>
);

const SheetPreview = ({ issue, sections }) => {
  const [open, setOpen] = useState(true);
  const t = issue.totals || {};
  const lines = issue.lines || [];
  return (
    <div className="mt-3 border-t border-slate-800 pt-3" data-testid="new-sheet-preview">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-400">
        {[["income", "Revenue"], ["ebitda", "EBITDA"], ["project_capex", "Project capex"], ["ncf", "NCF"]].map(([k, l]) => (
          <span key={k}>{l} Σ <span className="num text-slate-100" data-testid={`new-sheet-total-${k}`}>{fmt(t[k])}</span></span>
        ))}
        <span className={issue.checks_ok ? "text-emerald-400" : "text-amber-400"}>{issue.checks_ok ? "Totals reconcile" : "Some totals do not reconcile"}</span>
        <button onClick={() => setOpen((o) => !o)} className="ml-auto text-amber-400 hover:text-amber-300" data-testid="new-sheet-preview-toggle">
          {open ? "Hide" : "Show"} {lines.length} line items
        </button>
      </div>
      {open && (
        <div className="mt-2 max-h-72 overflow-auto rounded border border-slate-800">
          <table className="w-full text-xs" data-testid="new-sheet-lines-table">
            <thead className="sticky top-0 bg-[#0f172a] text-left text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-3 py-1.5">Section</th><th className="px-3 py-1.5">Line item</th><th className="px-3 py-1.5 text-right">FY {issue.years?.[0] || "Y1"}</th><th className="px-3 py-1.5 text-right">Σ all years</th></tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-t border-slate-800">
                  <td className="px-3 py-1 text-slate-400">{l.section === "loan_details" ? "Loan facility" : sections[l.section]}</td>
                  <td className="px-3 py-1 text-slate-100">{l.label}{l.group && <span className="ml-1 text-slate-500">· {l.group}</span>}</td>
                  <td className="num px-3 py-1 text-right">{fmt(l.first)}</td>
                  <td className="num px-3 py-1 text-right">{fmt(l.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const IssueHead = ({ issue }) => (
  <div className="min-w-0">
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-300">{TITLES[issue.type]}</span>
      <span className="font-medium text-slate-100">{issue.label}</span>
      {issue.sheet && <span className="text-xs text-slate-500">{issue.sheet}{issue.section ? ` · ${issue.section}` : ""}</span>}
      {issue.total != null && <span className="num text-xs text-amber-400">Σ {fmt(issue.total)}</span>}
    </div>
    <div className="mt-1 text-xs text-slate-500">{issue.message}</div>
    {issue.duplicate_of && <div className="mt-1 text-xs text-rose-400" data-testid="review-duplicate-title-warning">Sheet title matches existing entity “{issue.duplicate_of}”.</div>}
  </div>
);
